import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { formatCurrency } from "@/lib/money";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import type { RevenueByYearReport, YearlyMonthRevenue, YearlyReport } from "@/types/camping";
import { StatCard, downloadCsv, DeltaBadge, LogicNote, MONTH_LABELS, percentChange } from "@/pages/reports/reportShared";

const QUARTERS = [
  { label: "Q1", months: [1, 2, 3] as const },
  { label: "Q2", months: [4, 5, 6] as const },
  { label: "Q3", months: [7, 8, 9] as const },
  { label: "Q4", months: [10, 11, 12] as const },
] as const;

function sumMonthAmounts(
  months: YearlyMonthRevenue[],
  monthNumbers: readonly number[],
): number {
  return monthNumbers.reduce((sum, monthNum) => {
    const row = months.find((m) => m.month === monthNum);
    return sum + (Number.parseFloat(row?.invoiced_total ?? "0") || 0);
  }, 0);
}

function RevenueByYearBarChart({
  years,
  currency,
}: {
  years: RevenueByYearReport["years"];
  currency: string;
}) {
  const max = useMemo(() => {
    const values = years.map((y) => Number.parseFloat(y.invoiced_total) || 0);
    return Math.max(...values, 1);
  }, [years]);

  return (
    <div className="space-y-4" role="img" aria-label="Jahresumsätze im Vergleich">
      <div className="overflow-x-auto">
        <div
          className="flex items-end gap-2"
          style={{ minWidth: `${Math.max(years.length, 1) * 3.25}rem` }}
        >
          {years.map((item) => {
            const amount = Number.parseFloat(item.invoiced_total) || 0;
            const height = Math.max(amount > 0 ? 4 : 0, (amount / max) * 100);
            const isLatest = item.year === years[years.length - 1]?.year;
            return (
              <div key={item.year} className="flex min-w-10 flex-1 flex-col items-center gap-1">
                <div className="flex h-36 w-full items-end justify-center">
                  <div
                    className={isLatest ? "w-full max-w-10 rounded-t bg-accent" : "w-full max-w-10 rounded-t bg-accent/55"}
                    style={{ height: `${height}%` }}
                    title={formatCurrency(item.invoiced_total, currency)}
                  />
                </div>
                <span className={isLatest ? "text-[10px] font-semibold text-accent sm:text-xs" : "text-[10px] text-label-secondary sm:text-xs"}>
                  {item.year}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function YearMonthQuarterBreakdown({
  months,
  currency,
  yearTotal,
}: {
  months: YearlyMonthRevenue[];
  currency: string;
  yearTotal: string;
}) {
  return (
    <table className="eh-table w-full text-sm">
      <thead>
        <tr>
          <th className="text-left font-normal text-label-secondary">Monat</th>
          <th className="text-right font-normal text-label-secondary">Umsatz</th>
        </tr>
      </thead>
      <tbody>
        {QUARTERS.map((quarter) => (
          <Fragment key={quarter.label}>
            {quarter.months.map((monthNum) => {
              const row = months.find((m) => m.month === monthNum);
              const amount = row?.invoiced_total ?? "0";
              return (
                <tr key={`${quarter.label}-${monthNum}`}>
                  <td className="pl-4 text-label-secondary">
                    {MONTH_LABELS[monthNum - 1]}
                  </td>
                  <td className="text-right tabular-nums">
                    {formatCurrency(amount, currency)}
                  </td>
                </tr>
              );
            })}
            <tr className="bg-surface-secondary/60 font-medium">
              <td className="pl-2">{quarter.label} gesamt</td>
              <td className="text-right tabular-nums">
                {formatCurrency(sumMonthAmounts(months, quarter.months), currency)}
              </td>
            </tr>
          </Fragment>
        ))}
        <tr className="border-t border-separator font-semibold">
          <td>Jahr gesamt</td>
          <td className="text-right tabular-nums">{formatCurrency(yearTotal, currency)}</td>
        </tr>
      </tbody>
    </table>
  );
}

export function RevenueByYearSection() {
  const [report, setReport] = useState<RevenueByYearReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedYear, setExpandedYear] = useState<number | null>(null);
  const [yearDetails, setYearDetails] = useState<Record<number, YearlyReport>>({});
  const [detailLoadingYear, setDetailLoadingYear] = useState<number | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<RevenueByYearReport>("/api/v1/reports/revenue-by-year")
      .then(setReport)
      .catch((err: unknown) => {
        setReport(null);
        setError(
          err instanceof Error ? err.message : "Jahresumsätze konnten nicht geladen werden.",
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadYearDetail = useCallback(
    (year: number) => {
      if (yearDetails[year]) return;
      setDetailLoadingYear(year);
      setDetailError(null);
      api
        .get<YearlyReport>(`/api/v1/reports/yearly?year=${year}`)
        .then((data) => {
          setYearDetails((prev) => ({ ...prev, [year]: data }));
        })
        .catch((err: unknown) => {
          setDetailError(
            err instanceof Error
              ? err.message
              : `Monatsumsätze für ${year} konnten nicht geladen werden.`,
          );
        })
        .finally(() => setDetailLoadingYear(null));
    },
    [yearDetails],
  );

  const toggleYear = useCallback(
    (year: number) => {
      setDetailError(null);
      if (expandedYear === year) {
        setExpandedYear(null);
        return;
      }
      setExpandedYear(year);
      loadYearDetail(year);
    },
    [expandedYear, loadYearDetail],
  );

  const currency = report?.currency ?? "EUR";

  const latestYearChange = useMemo(() => {
    if (!report || report.years.length < 2) return null;
    const latest = report.years[report.years.length - 1]!;
    const prior = report.years[report.years.length - 2]!;
    const current = Number.parseFloat(latest.invoiced_total) || 0;
    const priorAmount = Number.parseFloat(prior.invoiced_total) || 0;
    return {
      year: latest.year,
      priorYear: prior.year,
      change: percentChange(current, priorAmount),
    };
  }, [report]);

  const exportCsv = () => {
    if (!report) return;
    const rows: string[][] = [
      ["Jahr", "Umsatz"],
      ...report.years.map((y) => [String(y.year), y.invoiced_total]),
      [],
      ["Gesamt", report.invoiced_total],
    ];
    const detail = expandedYear != null ? yearDetails[expandedYear] : undefined;
    if (detail) {
      rows.push([], [`Monatsumsätze ${detail.year}`], ["Monat", "Umsatz"]);
      for (const quarter of QUARTERS) {
        for (const monthNum of quarter.months) {
          const row = detail.months.find((m) => m.month === monthNum);
          rows.push([
            MONTH_LABELS[monthNum - 1] ?? String(monthNum),
            row?.invoiced_total ?? "0",
          ]);
        }
        rows.push([
          `${quarter.label} gesamt`,
          String(sumMonthAmounts(detail.months, quarter.months)),
        ]);
      }
    }
    downloadCsv("jahresumsaetze.csv", rows);
  };

  const expandedDetail = expandedYear != null ? yearDetails[expandedYear] : undefined;

  return (
    <section className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-label">Jahresumsätze</h2>
          <p className="mt-1 text-sm text-label-secondary">
            Umsatzentwicklung über alle Jahre mit ausgestellten Rechnungen
          </p>
        </div>
        <button
          type="button"
          className="eh-btn eh-btn--secondary min-h-[44px] whitespace-nowrap"
          disabled={!report || report.years.length === 0}
          onClick={exportCsv}
        >
          CSV exportieren
        </button>
      </header>

      {error ? (
        <p className="rounded-lg border border-status-red/30 bg-status-red/10 px-4 py-3 text-sm text-status-red">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Jahresumsätze werden geladen…</p>
      ) : report && report.years.length > 0 ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <StatCard
              label="Gesamtumsatz (alle Jahre)"
              value={formatCurrency(report.invoiced_total, currency)}
              hint={`${report.years.length} Jahre mit Rechnungsumsatz`}
            />
            {latestYearChange ? (
              <StatCard
                label={`Umsatz ${latestYearChange.year}`}
                value={formatCurrency(
                  report.years[report.years.length - 1]!.invoiced_total,
                  currency,
                )}
                hint={
                  latestYearChange.change
                    ? `Vorjahr ${latestYearChange.priorYear}: ${latestYearChange.change}`
                    : undefined
                }
              />
            ) : (
              <StatCard
                label={`Umsatz ${report.years[0]!.year}`}
                value={formatCurrency(report.years[0]!.invoiced_total, currency)}
                hint="Erstes Jahr mit Rechnungsumsatz"
              />
            )}
          </div>

          <section className="rounded-xl border border-separator bg-surface p-4 shadow-sm">
            <h3 className="font-medium text-label">Umsatz je Jahr</h3>
            <div className="mt-4">
              <RevenueByYearBarChart years={report.years} currency={currency} />
            </div>
          </section>

          <section className="overflow-x-auto rounded-xl border border-separator bg-surface shadow-sm">
            <p className="border-b border-separator px-4 py-2 text-xs text-label-secondary">
              Jahr anklicken für Monats- und Quartalsumsätze
            </p>
            <table className="eh-table w-full min-w-[20rem] text-sm">
              <thead>
                <tr>
                  <th className="text-left">Jahr</th>
                  <th className="text-right">zum Vorjahr</th>
                  <th className="text-right">Umsatz</th>
                </tr>
              </thead>
              <tbody>
                {report.years.map((row, index) => {
                  const isExpanded = expandedYear === row.year;
                  const previous = index > 0 ? report.years[index - 1] : null;
                  return (
                    <Fragment key={row.year}>
                      <ClickableTableRow
                        ariaLabel={`Jahr ${row.year}: Monats- und Quartalsumsätze ${isExpanded ? "ausblenden" : "anzeigen"}`}
                        className={isExpanded ? "bg-accent/5" : undefined}
                        onActivate={() => toggleYear(row.year)}
                      >
                        <td>
                          <span className="inline-flex items-center gap-2">
                            <span
                              className="inline-block w-4 text-label-tertiary"
                              aria-hidden
                            >
                              {isExpanded ? "▾" : "▸"}
                            </span>
                            {row.year}
                          </span>
                        </td>
                        <td className="text-right">
                          {previous ? (
                            <span className="inline-flex justify-end">
                              <DeltaBadge
                                current={Number.parseFloat(row.invoiced_total) || 0}
                                prior={Number.parseFloat(previous.invoiced_total) || 0}
                              />
                            </span>
                          ) : (
                            <span className="text-label-tertiary">—</span>
                          )}
                        </td>
                        <td className="text-right tabular-nums">
                          {formatCurrency(row.invoiced_total, currency)}
                        </td>
                      </ClickableTableRow>
                      {isExpanded ? (
                        <tr key={`${row.year}-detail`}>
                          <td colSpan={3} className="bg-surface-secondary/30 p-0">
                            {detailError && detailLoadingYear !== row.year ? (
                              <p className="px-4 py-3 text-sm text-status-red">{detailError}</p>
                            ) : detailLoadingYear === row.year && !expandedDetail ? (
                              <p className="px-4 py-3 text-sm text-label-secondary">
                                Monatsumsätze werden geladen…
                              </p>
                            ) : expandedDetail ? (
                              <div className="px-2 py-3 sm:px-4">
                                <p className="mb-2 px-2 text-xs font-medium text-label-secondary">
                                  Monats- und Quartalsumsätze {row.year}
                                </p>
                                <YearMonthQuarterBreakdown
                                  months={expandedDetail.months}
                                  currency={currency}
                                  yearTotal={row.invoiced_total}
                                />
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
                <tr className="border-t border-separator font-medium">
                  <td>Gesamt</td>
                  <td />
                  <td className="text-right tabular-nums">
                    {formatCurrency(report.invoiced_total, currency)}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <LogicNote>
            <p>{report.logic_note}</p>
          </LogicNote>
        </>
      ) : report ? (
        <p className="text-sm text-label-secondary">
          Noch keine Rechnungsumsätze für eine Jahresauswertung vorhanden.
        </p>
      ) : null}
    </section>
  );
}
